import type {
  DetailsSuccess,
} from '@autobid/strapi-integration/typescript/auction/detailsCar'
import type { ItemDeleteEvent } from '@autobid/ui/types/websocket/ItemDeleteEvent'
import type { ItemWithdrawEvent } from '@autobid/ui/types/websocket/ItemWithdrawEvent'
import { useWebSocketStore } from '@autobid/nuxt-pinia-store/store/useWebSocketStore'
import {
  carDetailsQueryKey,
} from '@autobid/ui/composables/car/data/useDetailsCar'
import { DETAILS_SOCKET_CALLBACK } from '@autobid/ui/composables/car/sockets/useDetailsSocket'
import { getIdFromSlug } from '@autobid/ui/utils/car/getIdFromSlug'
import { randomDebounce } from '@autobid/ui/utils/randomDebounce'
import { useQueryClient } from '@tanstack/vue-query'

export function useDetailsItemWithdrawOrDelete() {
  const { startListen } = useWebSocketStore()
  const queryClient = useQueryClient()

  const itemWithdrawOrDelete = () => {
    const updateCar = (event: ItemWithdrawEvent | ItemDeleteEvent) => {
      const queryKey = carDetailsQueryKey.byCarId(event.context.itemId)

      queryClient.setQueryData(
        queryKey,
        (data?: DetailsSuccess['data']['singleItem']): DetailsSuccess['data']['singleItem'] | undefined => {
          if (!data) {
            return data
          }

          return {
            ...data,
            item: {
              ...data.item,
              itemData: {
                ...data.item.itemData,
                stage: 'IN_PREPARATION',
                publicationInfo: {
                  ...data.item.itemData.publicationInfo,
                  status: 'WITHDRAW',
                },
              },
            },
          }
        },
      )
    }

    /**
     * Invalidate cars that contains the withdrawn car in its metadata pagination
     */
    const invalidateCarsWithCarInPagination = (
      event: ItemWithdrawEvent | ItemDeleteEvent,
    ) => {
      const { itemId } = event.context

      queryClient.invalidateQueries({
        predicate: (query) => {
          if (!Array.isArray(query.queryKey))
            return false

          if (query.queryKey[0] !== 'details')
            return false

          const queryData = query.state
            .data as DetailsSuccess['data']['singleItem']
          if (!queryData)
            return false

          const nextCarId = getIdFromSlug(queryData.metadata.next || '')
          const prevCarId = getIdFromSlug(queryData.metadata.previous || '')

          const hasThisCarInPagination
            = itemId === nextCarId || itemId === prevCarId

          return hasThisCarInPagination
        },
      })
    }

    const callback = (event: ItemWithdrawEvent | ItemDeleteEvent) => {
      updateCar(event)
      randomDebounce(0, 2000, () => {
        invalidateCarsWithCarInPagination(event)
      })
    }

    startListen({
      eventName: 'premium.ITEM_WITHDRAW',
      callbackName: DETAILS_SOCKET_CALLBACK,
      callback: event => callback(event),
    })

    startListen({
      eventName: 'premium.ITEM_DELETED',
      callbackName: DETAILS_SOCKET_CALLBACK,
      callback: event => callback(event),
    })
  }

  return { itemWithdrawOrDelete }
}
